/**
 * QMP-FE-6 (F3) — WHAT CHANGED SINCE THE REVISION BEFORE (D14.10).
 *
 * `GET /qmp` carries what the CURRENT revision froze for each package, but not
 * what the one before it said. Two reads answer that, and they are read once
 * per revision, not once per package a person opens:
 *
 *   * revision N  -> `diff_from_previous[qwp_id] = {sections_changed[],
 *     previous_rules_hash}` — the server's statement of which sections moved,
 *     derived from two immutable rows. `previous_rules_hash: null` means the
 *     revision before did not cover the package: it is new, and nothing is
 *     struck through.
 *   * revision N-1 -> `packages[qwp_id].rules` — the rules the strike-through
 *     prints. Not fetched for Revision 1, which has no "before".
 *
 * A read that fails leaves the diff empty: the sheet then shows the rules
 * being approved with nothing struck, which is the truthful fallback — it
 * never guesses that something changed.
 */
import { useEffect, useRef, useState } from 'react';
import { getQmpRevision } from '../lib/api';

const EMPTY = { diff: {}, previousPackages: {}, loading: false, error: '' };

export default function useQmpRevisionDiff(projectId, revision) {
  const [state, setState] = useState(EMPTY);
  const seq = useRef(0);

  useEffect(() => {
    const n = Number(revision || 0);
    if (!projectId || !n) { setState(EMPTY); return undefined; }
    seq.current += 1;
    const mine = seq.current;
    setState({ ...EMPTY, loading: true });
    Promise.all([
      getQmpRevision(projectId, n),
      n > 1 ? getQmpRevision(projectId, n - 1) : Promise.resolve(null),
    ]).then(([cur, prev]) => {
      if (mine !== seq.current) return;
      setState({
        diff: (cur && cur.data && cur.data.diff_from_previous) || {},
        previousPackages: (prev && prev.data && prev.data.packages) || {},
        loading: false,
        error: '',
      });
    }).catch(() => {
      if (mine !== seq.current) return;
      // Nothing is struck rather than something invented.
      setState({ ...EMPTY, error: 'Could not read what changed since the previous revision' });
    });
    return () => { seq.current += 1; };
  }, [projectId, revision]);

  return state;
}
